import { AES, enc } from 'crypto-js';
import { addDays } from 'date-fns';
import { isNumber } from 'lodash-es';
import { v4 } from 'uuid';

import { LocalStorageKey } from 'src/constants/common.constants';
import { isProduction } from 'src/constants/firebase.constants';

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * 현재 한국 시간 Date 객체를 반환한다. (UTC 메서드로 KST 값을 읽을 수 있다)
 */
const getKSTNow = (): Date => new Date(Date.now() + KST_OFFSET_MS);

/**
 * 현재 한국 날짜를 yyyy-MM-dd 형식으로 반환한다.
 */
const getKSTDate = (): string => {
  const kst = getKSTNow();
  return `${kst.getUTCFullYear()}-${pad(kst.getUTCMonth() + 1)}-${pad(kst.getUTCDate())}`;
};

/**
 * 한국 시간대 기준 세션 ID를 생성한다.
 * 포맷: yyyy-MM-dd_HH:mm:ss_xxxx (xxxx: 랜덤 4자리)
 */
const generateSessionId = (): string => {
  const kst = getKSTNow();
  const date = getKSTDate();
  const time = `${pad(kst.getUTCHours())}:${pad(kst.getUTCMinutes())}:${pad(kst.getUTCSeconds())}`;
  const random = v4().slice(0, 4);
  return `${date}_${time}_${random}`;
};

/**
 * localStorage에서 세션 ID를 가져오거나 새로 생성한다.
 * 날짜가 바뀌면 새 세션을 시작한다.
 */
const initSessionId = (): string => {
  const today = getKSTDate();
  const savedDate = localStorage.getItem(LocalStorageKey.sessionDate);
  const savedSessionId = localStorage.getItem(LocalStorageKey.sessionId);

  if (savedSessionId && savedDate === today) {
    return savedSessionId;
  }

  const newSessionId = generateSessionId();
  localStorage.setItem(LocalStorageKey.sessionId, newSessionId);
  localStorage.setItem(LocalStorageKey.sessionDate, today);
  return newSessionId;
};

export let sessionId = initSessionId();

/**
 * 날짜가 바뀌었으면 세션을 갱신하고 새 ID를 반환한다.
 */
export const refreshSessionIfNeeded = (): string => {
  const today = getKSTDate();
  const savedDate = localStorage.getItem(LocalStorageKey.sessionDate);

  if (savedDate !== today) {
    sessionId = generateSessionId();
    localStorage.setItem(LocalStorageKey.sessionId, sessionId);
    localStorage.setItem(LocalStorageKey.sessionDate, today);
  }

  return sessionId;
};

export const formatNumber = (price: number | string | null, fraction = 6, replacer = '') => {
  if (price === null) {
    return replacer;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    maximumFractionDigits: fraction,
  }).format(isNumber(price) ? price : Number(price));
};

export const sleep = async (ms?: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const currencyToNumber = (value: string) => (value ? Number(value.replace(',', '')) : null);

/**
 * 한글 통화 문자열을 숫자로 파싱합니다. (InputNumber parser용)
 * "1,000 원" -> 1000
 */
export const parseKoreanCurrency = (value: string | number | undefined): number => {
  if (typeof value === 'number') return value;
  return Number(value?.replace(/원\s?|(,*)/g, '') || '0');
};

export async function getPublicIp() {
  try {
    const response = await fetch('https://api64.ipify.org?format=json', {
      cache: 'no-cache',
    });
    if (response.ok) {
      const body = await response.json();
      return body.ip as string;
    } else {
      return 'unknown';
    }
  } catch (error) {
    console.error('[getPublicIp]', error);
    return 'error';
  }
}

/**
 * {@link https://developer.mozilla.org/ko/docs/Web/JavaScript/Reference/Global_Objects/Promise/allSettled Promise.allSettled}와 유사한 기능이다.
 * 다만 에러를 좀 더 수월하게 처리하기 위해 만들었다.
 */
export async function handleErrorsForPromiseAll<T>(promises: Promise<T>[], errorName = '[Errors]') {
  const results = await Promise.allSettled(promises);
  const errors = results.filter((result) => result.status === 'rejected');
  if (errors.length) {
    throw new TypeError(errorName, {
      // 불필요한 TypeError 문구는 제거한다.
      cause: errors.map((error) => String((error as PromiseRejectedResult).reason).replace(/TypeError: /, '')),
    });
  }
  return results.map((result) => (result as PromiseFulfilledResult<T>).value);
}

/**
 * 성공 실패를 나눠서 받는다.
 */
export async function promiseAllSettled<T>(promises: Promise<T>[]) {
  const results = await Promise.allSettled(promises);
  const fulfilled = results.filter((result) => result.status === 'fulfilled');
  const rejected = results.filter((result) => result.status === 'rejected');
  return {
    fulfilled: fulfilled.map((result) => (result as PromiseFulfilledResult<T>).value),
    // 불필요한 TypeError 문구는 제거한다.
    rejected: rejected.map((error) => String((error as PromiseRejectedResult).reason).replace(/TypeError: /, '')),
  };
}

/**
 * 값이 undefined인 경우에 대한 처리를 추가한 JSON.parse
 */
export function jsonParseWithCatch<T>(value: string | null): T | undefined {
  try {
    return value === 'undefined' ? undefined : JSON.parse(value ?? 'null');
  } catch (error) {
    console.error(error);
    return undefined;
  }
}

export function setLocalStorageValue<T>(key: string, newValue: T) {
  localStorage.setItem(key, JSON.stringify(newValue));
}

export function getLocalStorageValue<T>(key: string) {
  const storedLocalValue = jsonParseWithCatch<T>(localStorage.getItem(key));
  return storedLocalValue;
}

export function removeLocalStorageValue(key: string) {
  localStorage.removeItem(key);
}

export const textSort = (a: string, b: string) => {
  return a < b ? -1 : a > b ? 1 : 0;
};

export const parseIfObject = (value: any) => {
  if (typeof value === 'object') {
    return value instanceof Error ? JSON.stringify(value, Object.getOwnPropertyNames(value)) : JSON.stringify(value);
  }
  return value;
};

export const errorObjectToString = (error: any) => (error instanceof Error ? error.message : parseIfObject(error));

export const downloadJSON = (obj: object, filename: string) => {
  const downloadAnchorNode = document.createElement('a');
  const downloadUrl = URL.createObjectURL(new Blob([JSON.stringify(obj)], { type: 'text/json' }));
  downloadAnchorNode.setAttribute('href', downloadUrl);
  downloadAnchorNode.setAttribute('download', `${filename}.json`);
  document.body.appendChild(downloadAnchorNode); // required for firefox
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
};

const matchOperatorsRe = /[|\\{}()[\]^$+*?.]/g;
export const escapeStringRegExp = (str: string) => {
  return new RegExp(str.replace(matchOperatorsRe, '\\$&'));
};

export const downloadFromUrl = (url: string, filename: string) => {
  const downloadAnchorNode = document.createElement('a');
  downloadAnchorNode.setAttribute('href', url);
  downloadAnchorNode.setAttribute('download', filename);
  document.body.appendChild(downloadAnchorNode); // required for firefox
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
};

/**
 * value가 null, undefined, ''인 경우에는 null을 리턴한다.
 */
export const excelRowValidation = (value: string) => {
  return value ? (value.length > 0 ? value : null) : null;
};

/** 특정 문자를 배열의 앞으로 위치시킨다. */
export const moveItemsToFrontInArray = (arr: string[], items: string[]) => {
  const front = items.filter((i) => arr.includes(i));
  const rear = arr.filter((i) => !items.includes(i));
  return [...front, ...rear];
};

export const encryptAES = (text: string) => {
  const key = process.env.REACT_APP_PROJECT_ID;
  if (!key) {
    throw new Error('No key');
  }
  return AES.encrypt(text, key).toString();
};

export const decryptAES = (text: string) => {
  const key = process.env.REACT_APP_PROJECT_ID;
  if (!key) {
    throw new Error('No key');
  }
  return AES.decrypt(text, key).toString(enc.Utf8);
};

export const encodeBase64 = (text: string) => {
  return enc.Base64.stringify(enc.Utf8.parse(text));
};

export const decodeBase64 = (text: string) => {
  return enc.Base64.parse(text).toString(enc.Utf8);
};

const getDataDogApplicationId = (serviceVersion: number | undefined) => {
  if (serviceVersion === 2) {
    return isProduction ? '75a0fa45-1437-4fbf-b41b-90db72c7c9c4' : '4b1c6d94-2b38-4426-818e-8ec8a1cc9f46';
  }
  return 'dc32f68b-6193-473b-8645-dd6d3760ce04';
};

/**
 * datadog에 바로갈 수 있는 링크를 생성한다.
 */
export const getDataDogLinkForUser = (
  email: string,
  serviceVersion: number | undefined,
  options?: {
    // 시작일 (timestamp)
    from?: string;
    // 끝
    to?: string;
  },
) => {
  const url = 'https://us3.datadoghq.com/product-analytics/replay/search';
  const applicationId = getDataDogApplicationId(serviceVersion);
  const baseQuery = `*${email}* @type:session @application.id:${applicationId} @session.is_replay_available:true @session.is_active:false`;

  const { from, to } = options ?? {};
  const from_ts = from ?? addDays(Date.now(), -7).getTime();
  const to_ts = to ?? Date.now();
  const detailQuery = `&refresh_mode=paused&from_ts=${from_ts}&to_ts=${to_ts}`;

  return window.open(url + '?query=' + encodeURIComponent(baseQuery) + detailQuery, '_blank');
};

/**
 * - 를 삽입한다.
 */
export function normalizeTel(telNo?: string) {
  if (telNo == null || telNo === '') {
    return '';
  }
  // 숫자 이외에는 모두 제외한다.
  telNo = telNo.replace(/[^0-9]/g, '');

  // 2018-11-15 부터는 050으로 변환해서 FS에 저장하기 때문에 불펼요할 수 있다.
  telNo = telNo.replace(/^090/, '050');

  // 010- , 070-
  let matches = telNo.match(/^(0[17][01])(.{3,4})(.{4})$/);
  if (matches) {
    return `${matches[1]}-${matches[2]}-${matches[3]}`;
  }

  // 050은 4자리 식별번호를 사용하지만 3자리가 익숙하니 12자리가 아닌 경우에는 050에서 끊어준다.
  // 050-AAA?-BBBB
  matches = telNo.match(/^(050)(.{3,4})(.{4})$/);
  if (matches) {
    return `${matches[1]}-${matches[2]}-${matches[3]}`;
  }

  // 050X-AAAA-BBBB
  matches = telNo.match(/^(050.)(.{4})(.{4})$/);
  if (matches) {
    return `${matches[1]}-${matches[2]}-${matches[3]}`;
  }

  matches = telNo.match(/^(02)(.{3,4})(.{4})$/);
  if (matches) {
    return `${matches[1]}-${matches[2]}-${matches[3]}`;
  }

  matches = telNo.match(/^(0..)(.{3,4})(.{4})$/);
  if (matches) {
    return `${matches[1]}-${matches[2]}-${matches[3]}`;
  }

  return telNo;
}

/**
 * 마우스 클릭시 부모가 사라지면서 붙여놨던 onClick이벤트가 발생하지 않는 문제를 해결하기 위해
 * 기본 이벤트를 막아야한다.
 */
export const preventMouseDown = (e: any) => {
  e.preventDefault();
  e.stopPropagation();
};

export const phoneNumberRegex = /^010-?\d{3,4}-?\d{4}$/;

/**
 * 숫자의 소수점 자리수를 구한다.
 */
export const getDecimalLength = (num: number) => {
  const str = num.toString();
  const dotIndex = str.indexOf('.');

  if (dotIndex === -1) return 0;

  return str.length - dotIndex - 1;
};

export const getStepFromNumber = (num: number) => {
  const decimalLength = getDecimalLength(num);

  return 1 / Math.pow(10, decimalLength);
};

/**
 * NHN에서 허용하는 바이트 기준을 맞추기 위한 계산 코드
 * 문자 크기 계산은 UTF-8이 아닌 EUC-KR을 기준으로 한다.
 */
export const getByteSizeForEUCKR = (str: string) =>
  [...str].reduce((byteSize, char) => {
    const codePoint = char.charCodeAt(0);

    if (codePoint <= 0x007f) {
      return byteSize + 1;
    }
    return byteSize + 2;
  }, 0);

/**
 * 원단위를 10원 단위로 반올림한다.
 */
export const roundToNearestTen = (num: number) => Math.round(num / 10) * 10;

export const storeCodeRegex = /R(\d{5})/;

/**
 * redux & ga에서 사용불가한
 * Timestamp를 제거한다.
 */
export const deleteTimestamp = <T>(data: any) => {
  // 원본을 그대로 반환하면 redux(immer)가 그 객체를 freeze 해버려서
  // 원본을 들고 있는 쪽(ag-grid rowData 등)의 수정이 막힌다. 반드시 복사본을 반환한다.
  const copy = { ...data };
  delete copy._timeUpdate;
  delete copy._timeCreate;
  delete copy._timeDelete;
  return copy as T;
};

/**
 * 한글, 영문, 숫자만 남기고 나머지는 제거한다.
 */
export function sanitizeStringForLang(input: string): string {
  return input.replace(/[^가-힣a-zA-Z0-9]/g, '');
}

/**
 * 대상 id로 스크롤해준다.
 * @param offset 스크롤 위치에서 빼줄 offset (예: sticky 헤더 높이)
 */
export const scrollToElement = (targetId: string, containerRef: React.RefObject<HTMLDivElement>, offset = 0) => {
  const element = document.getElementById(targetId);
  if (element && containerRef.current) {
    const scrollTop = element.offsetTop - containerRef.current.offsetTop - offset;
    containerRef.current.scrollTo({
      top: scrollTop,
      behavior: 'smooth',
    });
  }
};

/**
 * 버전 문자열을 비교합니다.
 * @param currentVersion 현재 버전 (예: "20250625-152829")
 * @param minConsoleVersion 최소 요구 버전 (예: "20250625-100000")
 * @returns 현재 버전이 최소 버전보다 작으면 true, 그렇지 않으면 false
 */
export const isVersionLessThan = (currentVersion: string, minConsoleVersion: string): boolean => {
  if (!currentVersion || !minConsoleVersion) {
    return false;
  }

  // 버전 문자열을 숫자로 변환하여 비교
  const currentVersionNum = parseInt(currentVersion.replace('-', ''), 10);
  const minVersionNum = parseInt(minConsoleVersion.replace('-', ''), 10);

  return currentVersionNum < minVersionNum;
};
